// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { createResilientFetch } from '@/lib/resilient-fetch';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

// Extract current project ID from URL 
function extractProjectId(url: string): string | null {
  const match = url.match(/https:\/\/([a-z0-9]+)\.supabase\.co/);
  return match ? match[1] : null;
}

// Clean up stale Supabase auth tokens from other projects
function cleanupStaleAuthTokens(): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return; // Not running in browser environment
    }

    const currentProjectId = extractProjectId(SUPABASE_URL);
    if (!currentProjectId) {
      return; // Could not extract project ID
    }

    const currentAuthTokenKey = `sb-${currentProjectId}-auth-token`;
    const allKeys = Object.keys(localStorage);
    const staleTokens = allKeys.filter(
      (key) =>
        key.match(/^sb-[a-z0-9]+-auth-token$/) && // Matches Supabase auth token pattern
        key !== currentAuthTokenKey // But is NOT the current project's token
    );

    if (staleTokens.length > 0) {
      console.log(
        '[Supabase] Cleaning up %d stale auth token(s) from previous projects:',
        staleTokens.length
      );
      staleTokens.forEach((key) => {
        localStorage.removeItem(key);
        console.log('[Supabase] ✓ Removed stale token:', key);
      });
    }
  } catch (error) {
    // Silently fail - don't disrupt app startup
    console.warn('[Supabase] Failed to cleanup stale tokens:', error);
  }
}

// Run cleanup on module load
cleanupStaleAuthTokens();

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// Create base Supabase client with timeout and retry configuration.
// Idempotent requests are transparently re-sent when the keep-alive connection
// to Supabase has been dropped (net::ERR_CONNECTION_CLOSED / "Failed to fetch").
export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  global: {
    headers: {
      'X-Client-Info': 'meet-now-notes'
    },
    fetch: createResilientFetch((url, options) => fetch(url, options), {
      timeoutMs: 30000,
      maxRetries: 2,
      onRetry: ({ url, method, attempt, error }) => {
        console.warn(
          `[Supabase] Connection dropped (${(error as Error)?.message}); re-sending ${method} ${url.split('?')[0]} (retry ${attempt}/2)`
        );
      },
    }),
  },
  db: {
    schema: 'public'
  },
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  }
});

// Ensure session is restored from localStorage on client creation
// This is critical for auth to work properly
if (typeof window !== 'undefined') {
  (async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) {
        console.warn('[Supabase] Warning fetching session on init:', error.message);
      }
      if (session?.user) {
        console.log('[Supabase] Session restored on client init:', session.user.email);
      } else {
        console.log('[Supabase] No session found on client init - user needs to sign in');
      }
    } catch (err) {
      console.error('[Supabase] Error restoring session on init:', err);
    }
  })();
}

/**
 * Enhanced Supabase methods with automatic retry and timeout handling
 * This wrapper provides resilient database operations with exponential backoff
 */
export const createEnhancedQuery = () => {
  const maxRetries = 3;
  const baseDelay = 1000; // 1 second

  return {
    /**
     * Execute a query with automatic retry on failure
     */
    async withRetry<T>(
      queryName: string,
      queryFn: () => Promise<{ data: T | null; error: any }>
    ): Promise<{ data: T | null; error: any }> {
      let lastError: any = null;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const result = await queryFn();
          if (!result.error) {
            return result; // Success
          }
          lastError = result.error;

          // Don't retry on client errors (4xx)
          if (result.error?.status >= 400 && result.error?.status < 500) {
            return result;
          }
        } catch (error) {
          lastError = error;
        }

        // Calculate backoff delay
        if (attempt < maxRetries) {
          const delay = baseDelay * Math.pow(2, attempt - 1);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      return { data: null, error: lastError };
    }
  };
};